<template>
  <OwnDialog
    v-model="isVisible"
    class="upsell-modal-dialog"
    size="lg"
    @cancel="onDismiss"
  >
    <div
      class="upsell-modal grid gap-8 p-6 lg:grid-cols-[280px_1fr]"
      data-testid="upsell-modal"
    >
      <UpsellHero
        class="h-full"
        compact
        :title="title"
        :description="description"
        :price="price"
        :chat-text="t('upsell.maybe-later')"
        @chat="onDismiss"
        @upgrade="emit('upgrade')"
      />
      <div class="upsell-modal__feature-pane flex min-w-0 flex-col gap-6">
        <UpsellFeatures variant="list" :features="features" />
        <OwnButton
          class="upsell-modal__compare-button"
          small
          :text="t('upsell.modal-compare')"
          data-testid="upsell-modal-compare-button"
          @click="scrollToComparison"
        >
          <template #icon-right>
            <ChevronBottom :size="16" />
          </template>
        </OwnButton>
        <PlanComparisonTable
          ref="comparisonTable"
          :plans="plans"
          :sections="sections"
        />
      </div>
    </div>
  </OwnDialog>
</template>

<script lang="ts" setup>
import { ChevronBottom } from '@owner/icons-vue/ChevronBottom'
import { useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'

import { OwnButton } from '@/ui/OwnButton'
import { OwnDialog } from '@/ui/OwnDialog'

import { useMaxUpsellContent } from '../composables/useMaxUpsellContent'

import PlanComparisonTable from './PlanComparisonTable.vue'
import UpsellFeatures from './UpsellFeatures.vue'
import UpsellHero from './UpsellHero.vue'

const emit = defineEmits<{
  upgrade: []
}>()

const isVisible = defineModel<boolean>()

const { t } = useI18n({ useScope: 'global' })

const { description, features, plans, price, sections, title } =
  useMaxUpsellContent()

const comparisonTable = useTemplateRef<InstanceType<
  typeof PlanComparisonTable
> | null>('comparisonTable')

const onDismiss = (): void => {
  isVisible.value = false
}

const scrollToComparison = (): void => {
  comparisonTable.value?.$el?.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  })
}
</script>

<style lang="scss" scoped>
// The dialog card is full-width below the kit's lg breakpoint — cap it at the
// design's compact width on every viewport.
.upsell-modal-dialog :deep(.own-dialog__card) {
  max-width: 760px;
  background-color: var(--background-secondary);
}

.upsell-modal {
  &__feature-pane {
    max-height: 440px;
    overflow-y: auto;
  }

  .upsell-modal__compare-button {
    color: var(--text-color-secondary);

    :deep(.text-button-regular) {
      font-size: 0.875rem;
      line-height: 1.125rem;
    }

    background-color: var(--background-quarternary);
    box-shadow: none;

    &:hover {
      background-color: var(--background-secondary);
    }
  }
}
</style>
